import { Link } from 'react-router-dom';
import { DIRECT_DISCOUNT_PCT } from '../lib/property-schema.js';
import Footer from '../components/Footer.jsx';
import Header from '../components/Header.jsx';
import Seo from '../components/Seo.jsx';
import { Eyebrow } from '../components/Section.jsx';
import { properties, propertyPhotos } from '../lib/property-config.js';

// The host-neutral booking surface. This page is why a "Stay" link can live in
// the shared masthead at all: it resolves to a market index, not to whichever
// property happens to be first in property-config. The brief stays the city's
// asset (commit 100199b), and the conversion path stops privileging one host.
//
// With one property this is a one-item list, which is honest rather than
// embarrassing: the copy says so plainly instead of implying a catalogue.

function PropertyCard({ property }) {
  const [lead] = propertyPhotos(property);
  const { rating, count } = property.reviews || {};
  return (
    <li className="border-b border-navy-100 py-6 first:border-t first:border-navy-100">
      <Link
        to={`/stay/${property.slug}`}
        className="group flex flex-col gap-4 no-underline sm:flex-row sm:gap-5"
      >
        {lead && (
          <div className="shrink-0 overflow-hidden rounded-lg sm:w-52">
            {/* The large descriptor is the file's own width. Three of these
                photos are portrait and narrower than 1200, so the hardcode
                described candidates that do not exist. */}
            <img
              src={lead.src640}
              srcSet={`${lead.src640} 640w, ${lead.src} ${lead.width}w`}
              sizes="(min-width: 640px) 208px, 100vw"
              width={lead.width}
              height={lead.height}
              alt={lead.alt}
              loading="lazy"
              decoding="async"
              className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-[1.02] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
            />
          </div>
        )}
        <div className="min-w-0">
          <h2 className="font-display text-xl font-semibold text-navy-900 group-hover:underline sm:text-2xl">
            {property.name}
          </h2>
          <p className="mt-1 text-sm text-navy-600">
            {property.bedrooms} bed · {property.bathrooms} bath · sleeps {property.sleeps} ·{' '}
            {property.location}
          </p>
          {rating && count && (
            <p className="mt-1.5 text-sm font-medium text-navy-800">
              {rating}
              <span className="text-cream-600">★</span> across {count} reviews
            </p>
          )}
          <p className="mt-2.5 leading-relaxed text-navy-700">{property.headline}</p>
          <p className="mt-3 text-xs font-medium uppercase tracking-[0.16em] text-cream-600">
            See the house and dates <span aria-hidden="true">›</span>
          </p>
        </div>
      </Link>
    </li>
  );
}

export default function StayIndex() {
  const all = Object.values(properties);
  const one = all.length === 1;

  return (
    <div className="mx-auto max-w-brief px-4 pt-6 sm:px-6">
      <Seo
        title="Places to Stay in Charleston · Charleston.Vacations"
        description={`Homes to rent around Charleston, SC, booked direct with the hosts. ${DIRECT_DISCOUNT_PCT}% off nightly rates, no platform service fees, same calendars as Airbnb and Vrbo.`}
      />
      <Header />
      <div className="mt-8 border-b border-navy-100 pb-6">
        <Eyebrow text="Where to stay" />
        <h1 className="mt-2 font-display text-3xl font-semibold text-navy-900 sm:text-4xl">
          Places to stay in Charleston
        </h1>
        <p className="prose-navy mt-3 text-navy-700">
          {one
            ? `Homes you can book direct with the people who own them: ${DIRECT_DISCOUNT_PCT}% off nightly rates, no platform service fee on top. One so far, listed below. We would rather add hosts slowly and keep writing the brief for guests who actually stay in them.`
            : `Homes you can book direct with the people who own them: ${DIRECT_DISCOUNT_PCT}% off nightly rates, no platform service fee on top. Same calendars as Airbnb and Vrbo, lower totals, and a host who answers their own phone.`}
        </p>
      </div>

      <ul>
        {all.map((p) => (
          <PropertyCard key={p.slug} property={p} />
        ))}
      </ul>

      <p className="mt-6 text-sm leading-relaxed text-navy-700">
        Still deciding which part of town? The{' '}
        <Link to="/guides" className="text-navy-900 underline">
          Charleston trip guides
        </Link>{' '}
        weigh Mount Pleasant against downtown and the beaches, and{' '}
        <Link to="/" className="text-navy-900 underline">
          today's brief
        </Link>{' '}
        tells you what this particular day is good for.
      </p>

      <Footer />
    </div>
  );
}
